// eslint-disable-next-line no-redeclare
import _ from 'lodash';
import * as feature from '@hotjar/webapp/domains/common/feature';

/**
 * Functionality to decide if a feature can be used or not.
 * Check order: default features based on plan, site.features, org.features, account.features.
 *
 * See the feature _directive_ file for a full explanation for how this works.
 */
angular.module('hjApp.user').service('Feature', function ($rootScope, Account, Plan, UserData) {
    /**************************
     * PUBLIC ACCESS FUNCTIONS
     **************************/

    /**
     * Check if a feature can be used by a specific site.
     * This function checks in site, organization or account.
     *
     * @param featureName Name of feature.
     * @param site Site to check for. Defaults to UserData.currentSite.
     */
    this.isAllowed = (featureName, site) => feature.isAllowed(featureName, site || UserData.currentSite);

    /**
     * Check if a feature can be used by a specific site.
     *
     * @param feature Name of feature.
     * @param site Site to check for.
     */
    this.isSiteAllowed = (featureName, site) => feature.isSiteAllowed(featureName, site || UserData.currentSite);

    /**
     * Check if a feature can be used by a specific organization
     * which can possibly be under a different account.
     *
     * @param feature Name of feature.
     * @param organization Organization to check for.
     */
    this.isOrganizationAllowed = feature.isOrganizationAllowed;

    /**
     * Check if a feature can be used by a specific account.
     * NOTE: If no account is provided, this function will only work if the user is also the Account Owner.
     * @param feature Name of feature.
     * @param account Account to check for. Defaults to owned account.
     */
    this.isAccountAllowed = (featureName, account) =>
        feature.isAccountAllowed(featureName, account || Account.getOwned());

    /**
     * Return an object about limits for a specific feature.
     * @param feature Name of feature.
     * @param count The count is used to check if this limit has been reached.
     * @param site Site to check for. Defaults to UserData.currentSite.
     */
    this.getLimits = function (feature, count, site) {
        var plan,
            planLimit,
            limitReached = false;

        count = count || null;
        site = site || UserData.currentSite;

        // We might not have a site available. E.g. User is not logged in.
        if (!site) {
            return null;
        }

        plan = getSitePlan(site);
        planLimit = window.settings.plans[plan].limits[feature];

        if (!planLimit) {
            // There is no limit for this feature on this plan.
            planLimit = false;
        } else if (planLimit && count) {
            // There is a limit and we have a count. Check if the limit has been reached.
            limitReached = count >= planLimit;
        } else if (planLimit && count === null) {
            // There is a limit but we don't have the count available yet.
            limitReached = undefined;
        }

        return {
            applies: Boolean(planLimit),
            count: planLimit,
            reached: limitReached
        };
    };

    /**
     * Return the lowest tier plan a feature is available on, null if not available on any.
     * @param feature Name of feature.
     */
    this.plan = function (feature) {
        var lowestTierPlan;

        if (_.isUndefined(feature)) {
            throw new Error('Feature.plan(): You must specify (pass) a feature name to use this function.');
        }

        // Go through each plan and check that:
        // 1. This account has access to this plan.
        // 2. The feature is allowed on this plan.
        // 3. Is a lower plan than the lowest if we have one.
        _.each(window.settings.plans, function (plan, key) {
            if (
                _.includes(plan.features, feature) &&
                (_.isUndefined(lowestTierPlan) || plan.tier < lowestTierPlan.tier)
            ) {
                lowestTierPlan = {
                    name: key,
                    tier: plan.tier
                };
            }
        });

        if (lowestTierPlan) {
            return lowestTierPlan.name;
        }
    };

    /**
     * Get the minimum plan required for the given limit amount.
     */
    this.getMinimumFeatureGroupForLimit = feature.getMinimumFeatureGroupForLimit;

    /**
     * Returns the effective plan of the current site.
     * @param site Optional site to check for effective plan.
     * @returns {string} The effective plan of the current site.
     */
    this.getSitePlan = function (site) {
        site = site || UserData.currentSite;
        return getSitePlan(site);
    };

    /**
     * Returns the list of all features of the current site.
     * @param site Site to check for. Defaults to UserData.currentSite.
     * @returns {Array} The list of all features.
     */
    this.getAll = function (site) {
        site = site || UserData.currentSite;
        return feature.getAll(site);
    };

    /*******************
     * PRIVATE FUNCTIONS
     *******************/
    function getSitePlan(site) {
        return Plan.get(site).effective.name;
    }
});
